/**
 * Módulo de Administración de Facturación CFDI 4.0.
 *
 * Configuración de datos fiscales por sucursal e historial de facturas solicitadas.
 */
import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

interface Sucursal {
  id: number;
  nombre: string;
  tipo: 'restaurante' | 'abarrotes';
}

interface DatosFiscales {
  id?: number;
  razon_social: string;
  rfc: string;
  regimen_fiscal: string;
  codigo_postal: string;
  direccion: string | null;
  colonia: string | null;
  ciudad: string | null;
  entidad: string | null;
  email_emisor: string | null;
  id_empresa_fac: number | null;
  serie: string;
  folio_actual: number;
}

interface ItemHistorial {
  id: number;
  fecha: string;
  detalle: {
    referencia: string;
    serie: string;
    folio: number;
    total: number | string;
    pacEncolado: boolean;
    receptor: {
      rfc: string;
      razonSocial: string;
      codigoPostal: string;
      regimenFiscal: string;
      usoCfdi: string;
    };
  };
}

export function Facturacion({
  sucursalId,
  sucursales = [],
}: {
  sucursalId: number;
  sucursales?: Sucursal[];
}) {
  const [tab, setTab] = useState<'config' | 'historial'>('config');
  const [sucursalActivaId, setSucursalActivaId] = useState(sucursalId);

  const [fiscales, setFiscales] = useState<DatosFiscales | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [historial, setHistorial] = useState<ItemHistorial[]>([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const cargarDatosFiscales = useCallback(async () => {
    try {
      setCargando(true);
      const res = await api<DatosFiscales>(`/facturacion/sucursal?sucursalId=${sucursalActivaId}`);
      setFiscales(res);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }, [sucursalActivaId]);

  const cargarHistorial = useCallback(async () => {
    try {
      setCargandoHistorial(true);
      const res = await api<ItemHistorial[]>(`/facturacion/historial?sucursalId=${sucursalActivaId}`);
      setHistorial(res);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoHistorial(false);
    }
  }, [sucursalActivaId]);

  useEffect(() => {
    cargarDatosFiscales();
  }, [cargarDatosFiscales]);

  useEffect(() => {
    if (tab === 'historial') cargarHistorial();
  }, [tab, cargarHistorial]);

  async function guardarFiscales(e: React.FormEvent) {
    e.preventDefault();
    if (!fiscales) return;

    try {
      setGuardando(true);
      const res = await api<DatosFiscales>(`/facturacion/sucursal?sucursalId=${sucursalActivaId}`, {
        method: 'POST',
        body: fiscales,
      });
      setFiscales(res);
      alert('Datos fiscales de la sucursal actualizados.');
    } catch (err: any) {
      alert(err?.message || 'Error al guardar datos fiscales.');
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) {
    return <p className="py-24 text-center text-salvia font-bold">Cargando datos fiscales…</p>;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 font-cuerpo text-tinta">
      <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Facturación CFDI 4.0</h1>
          <p className="text-sm text-salvia">Datos del emisor y cola de timbrado del PAC</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {sucursales.length > 1 && (
            <select
              value={sucursalActivaId}
              onChange={(e) => setSucursalActivaId(Number(e.target.value))}
              className="campo text-sm py-2"
            >
              {sucursales.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          )}
          <div className="flex rounded-xl bg-cal p-1 border border-salvia/20">
          <button
            onClick={() => setTab('config')}
            className={`rounded-lg px-4 py-1.5 text-xs font-bold transition ${
              tab === 'config' ? 'bg-nopal text-white shadow-sm' : 'text-salvia hover:text-tinta'
            }`}
          >
            ⚙️ Configuración Emisor
          </button>
          <button
            onClick={() => setTab('historial')}
            className={`rounded-lg px-4 py-1.5 text-xs font-bold transition ${
              tab === 'historial' ? 'bg-nopal text-white shadow-sm' : 'text-salvia hover:text-tinta'
            }`}
          >
            📋 Facturas Solicitadas
          </button>
          </div>
        </div>
      </header>

      {tab === 'config' && fiscales ? (
        <form onSubmit={guardarFiscales} className="rounded-3xl border border-salvia/20 bg-white p-6 shadow-sm">
          <h2 className="font-titulo text-lg font-bold text-tinta mb-4">Datos del Emisor de la Sucursal</h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Razón Social / Nombre Emisor *</label>
              <input
                type="text"
                required
                value={fiscales.razon_social}
                onChange={(e) => setFiscales({ ...fiscales, razon_social: e.target.value })}
                className="campo text-base"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">RFC Emisor *</label>
              <input
                type="text"
                required
                maxLength={13}
                value={fiscales.rfc}
                onChange={(e) => setFiscales({ ...fiscales, rfc: e.target.value.toUpperCase() })}
                className="campo text-base font-bold uppercase"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Régimen Fiscal (3 dígitos) *</label>
              <input
                type="text"
                required
                maxLength={3}
                placeholder="Ej. 601"
                value={fiscales.regimen_fiscal}
                onChange={(e) => setFiscales({ ...fiscales, regimen_fiscal: e.target.value })}
                className="campo text-base"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Código Postal Fiscal *</label>
              <input
                type="text"
                required
                maxLength={5}
                value={fiscales.codigo_postal}
                onChange={(e) => setFiscales({ ...fiscales, codigo_postal: e.target.value })}
                className="campo text-base"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Serie de Facturación</label>
              <input
                type="text"
                maxLength={10}
                value={fiscales.serie}
                onChange={(e) => setFiscales({ ...fiscales, serie: e.target.value.toUpperCase() })}
                className="campo text-base uppercase"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Folio Actual Conservado</label>
              <input
                type="number"
                disabled
                value={fiscales.folio_actual}
                className="campo text-base bg-cal/60 font-bold"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">ID Empresa en Servidor PAC</label>
              <input
                type="number"
                placeholder="Ej. 1"
                value={fiscales.id_empresa_fac || ''}
                onChange={(e) =>
                  setFiscales({ ...fiscales, id_empresa_fac: e.target.value ? Number(e.target.value) : null })
                }
                className="campo text-base"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Correo Emisor de Notificaciones</label>
              <input
                type="email"
                placeholder="facturas@ericmar.com"
                value={fiscales.email_emisor || ''}
                onChange={(e) => setFiscales({ ...fiscales, email_emisor: e.target.value })}
                className="campo text-base"
              />
            </div>
          </div>

          <div className="mt-6 border-t border-salvia/15 pt-4 flex justify-end">
            <button disabled={guardando} type="submit" className="btn-principal py-3 px-8">
              {guardando ? 'Guardando…' : 'Guardar Cambios Fiscales'}
            </button>
          </div>
        </form>
      ) : (
        /* Pestaña Historial */
        <div className="rounded-3xl border border-salvia/20 bg-white p-6 shadow-sm">
          <h2 className="font-titulo text-lg font-bold text-tinta mb-4">Solicitudes Recientes de Factura</h2>

          {cargandoHistorial ? (
            <p className="py-8 text-center text-salvia font-bold">Cargando historial…</p>
          ) : historial.length === 0 ? (
            <p className="py-12 text-center text-salvia">No se han realizado solicitudes de factura todavía.</p>
          ) : (
            <div className="space-y-3">
              {historial.map((h) => (
                <div key={h.id} className="flex flex-col justify-between rounded-2xl border border-salvia/15 bg-cal/30 p-4 sm:flex-row sm:items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-titulo text-base font-bold text-tinta">
                        {h.detalle?.referencia || `Solicitud #${h.id}`}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          h.detalle?.pacEncolado
                            ? 'bg-nopal-claro text-nopal-oscuro'
                            : 'bg-miel/20 text-miel-oscuro'
                        }`}
                      >
                        {h.detalle?.pacEncolado ? 'Encolado PAC' : 'Modo Pruebas'}
                      </span>
                    </div>

                    <div className="mt-1 space-y-0.5 text-xs text-salvia">
                      <p>Receptor: <strong>{h.detalle?.receptor?.razonSocial}</strong> ({h.detalle?.receptor?.rfc})</p>
                      <p>CP: {h.detalle?.receptor?.codigoPostal} · Régimen: {h.detalle?.receptor?.regimenFiscal} · Uso: {h.detalle?.receptor?.usoCfdi}</p>
                      <p className="text-[10px]">{new Date(h.fecha).toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="mt-2 text-right sm:mt-0">
                    <span className="font-titulo text-lg font-extrabold text-nopal-oscuro">
                      ${Number(h.detalle?.total || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
